/**
 * FERP Portal - Module Overview Dashboard
 *
 * Default first view when opening any FERP Solution (module).
 * Shows entity type stats, compliance info, and quick navigation.
 *
 * Smart Navigation:
 * - When quickAccessCount > 0, splits entity cards into "Quick Access" and "All Types"
 * - "Quick Access" shows personalized frequent types or curated defaults
 * - Progressive enhancement: renders unchanged if quickAccessCount is 0/undefined
 *
 * FERP Principles:
 * - Data-driven: All stats from SubgraphEntityType metadata
 * - Dumb frontend: Only renders what metadata defines
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
  SimpleGrid,
  Card,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Badge,
  Button,
  Alert,
  Tooltip,
} from '@mantine/core';
import { IconShieldCheck, IconArrowRight, IconSparkles, IconAdjustments, IconScale } from '@tabler/icons-react';
import type { SubgraphEntityType } from '@/hooks/useTenantSubgraphConfig';
import { getIcon } from '@/utils/iconMap';

export interface ModuleOverviewProps {
  displayName: string;
  description: string;
  color: string;
  isLifeCritical: boolean;
  complianceFrameworks: string[];
  entityTypes: SubgraphEntityType[];
  onEntityClick: (entityType: SubgraphEntityType) => void;
  /** Smart navigation: how many entity types are in the quick access group */
  quickAccessCount?: number;
  /** Smart navigation: whether ordering is personalized vs curated */
  isPersonalized?: boolean;
  /** DRR-207: Function to get FSO override count for an entity type */
  getOverrideCount?: (entityType: string) => number;
  /** DRR-207: Function to get mandatory compliance info for an entity type */
  getMandatoryInfo?: (entityType: string) => { mandatory: boolean; statuteRef?: string } | undefined;
  /** DRR-207: Subgraph ID for override link generation */
  subgraphId?: string;
}

/** Entity type card with icon, description, and compliance badges */
function EntityCard({
  et,
  color,
  onClick,
  overrideCount,
  mandatoryInfo,
  subgraphId,
}: Readonly<{
  et: SubgraphEntityType;
  color: string;
  onClick: () => void;
  /** DRR-207: Number of FSO overrides for this entity type */
  overrideCount?: number;
  /** DRR-207: Mandatory compliance info */
  mandatoryInfo?: { mandatory: boolean; statuteRef?: string };
  /** DRR-207: Subgraph ID for override link */
  subgraphId?: string;
}>): React.ReactElement {
  const Icon = getIcon(et.icon);
  return (
    <Card
      withBorder
      padding="md"
      style={{ cursor: 'pointer' }}
      onClick={onClick}
    >
      <Group justify="space-between" mb="xs">
        <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
          {et.pluralName}
        </Text>
        <ThemeIcon size="md" variant="light" color={color}>
          <Icon size={16} />
        </ThemeIcon>
      </Group>
      <Text size="sm" c="dimmed" lineClamp={2}>
        {et.description}
      </Text>
      <Group mt="xs" gap={4}>
        {et.isLifeCritical && (
          <Badge size="xs" color="red" variant="light">Life-Critical</Badge>
        )}
        {/* DRR-207: Mandatory compliance badge */}
        {mandatoryInfo?.mandatory && (
          <Tooltip label={mandatoryInfo.statuteRef ? `Required by ${mandatoryInfo.statuteRef}` : 'Required for compliance'}>
            <Badge size="xs" color="orange" variant="filled" leftSection={<IconScale size={10} />}>
              Mandatory
            </Badge>
          </Tooltip>
        )}
        {et.complianceFrameworks.slice(0, 2).map((fw) => (
          <Badge key={fw} size="xs" variant="outline" color="gray">{fw}</Badge>
        ))}
        {/* DRR-207: FSO override badge — links to Form Overrides page */}
        {overrideCount !== undefined && overrideCount > 0 && (
          <Tooltip label="Jurisdiction-specific field overrides (click to view)">
            <Badge
              component={Link}
              to={`/configure/form-overrides?subgraph=${subgraphId ?? 'fhir'}&entity=${et.graphqlType}`}
              size="xs"
              color="indigo"
              variant="light"
              leftSection={<IconAdjustments size={10} />}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              style={{ cursor: 'pointer', textDecoration: 'none' }}
            >
              {overrideCount} override{overrideCount > 1 ? 's' : ''}
            </Badge>
          </Tooltip>
        )}
      </Group>
    </Card>
  );
}

export function ModuleOverview({
  displayName,
  description,
  color,
  isLifeCritical,
  complianceFrameworks,
  entityTypes,
  onEntityClick,
  quickAccessCount = 0,
  isPersonalized = false,
  getOverrideCount,
  getMandatoryInfo,
  subgraphId,
}: ModuleOverviewProps): React.ReactElement {
  // Split entity types into quick access and remaining groups
  const hasQuickAccess = quickAccessCount > 0;
  const quickAccessTypes = hasQuickAccess ? entityTypes.slice(0, quickAccessCount) : entityTypes;
  const remainingTypes = hasQuickAccess ? entityTypes.slice(quickAccessCount) : [];

  let sectionTitle: string | undefined;
  if (hasQuickAccess) {
    sectionTitle = isPersonalized ? 'Your Frequent Types' : 'Get Started';
  }

  return (
    <Stack gap="md">
      {/* Life-critical warning */}
      {isLifeCritical && (
        <Alert
          icon={<IconShieldCheck size={16} />}
          color="red"
          variant="light"
          title="Life-Critical Solution"
        >
          All operations in {displayName} are logged for compliance.
          Dual approval may be required for sensitive actions.
        </Alert>
      )}

      {/* Getting Started guidance for new tenants (DRR-073) */}
      {!isPersonalized && !hasQuickAccess && (
        <Alert
          icon={<IconSparkles size={16} />}
          color="blue"
          variant="light"
          title={`Welcome to ${displayName}`}
        >
          Browse the data types below to explore this solution.
          Your frequently accessed types will appear in a Quick Access section automatically.
        </Alert>
      )}

      {/* Quick Access / Primary entity type cards */}
      {sectionTitle && (
        <Text fw={600} size="sm" c="dimmed" tt="uppercase">{sectionTitle}</Text>
      )}
      <SimpleGrid cols={{ base: 2, sm: 3, md: Math.min(quickAccessTypes.length, 4) }} spacing="md">
        {quickAccessTypes.map((et) => (
          <EntityCard
            key={et.graphqlType}
            et={et}
            color={color}
            onClick={() => onEntityClick(et)}
            overrideCount={getOverrideCount?.(et.graphqlType)}
            mandatoryInfo={getMandatoryInfo?.(et.graphqlType)}
            subgraphId={subgraphId}
          />
        ))}
      </SimpleGrid>

      {/* Remaining entity types as compact buttons */}
      {remainingTypes.length > 0 && (
        <Card withBorder>
          <Text fw={600} mb="md">All Data Types</Text>
          <Group gap="sm">
            {remainingTypes.map((et) => {
              const Icon = getIcon(et.icon);
              return (
                <Button
                  key={et.graphqlType}
                  variant="light"
                  color={color}
                  leftSection={<Icon size={16} />}
                  rightSection={<IconArrowRight size={14} />}
                  onClick={() => onEntityClick(et)}
                  size="sm"
                >
                  {et.pluralName}
                </Button>
              );
            })}
          </Group>
        </Card>
      )}

      {/* Browse Data quick actions (when no quick access split) */}
      {!hasQuickAccess && (
        <Card withBorder>
          <Text fw={600} mb="md">Browse Data</Text>
          <Group gap="sm">
            {entityTypes.map((et) => {
              const Icon = getIcon(et.icon);
              return (
                <Button
                  key={et.graphqlType}
                  variant="light"
                  color={color}
                  leftSection={<Icon size={16} />}
                  rightSection={<IconArrowRight size={14} />}
                  onClick={() => onEntityClick(et)}
                  size="sm"
                >
                  {et.pluralName}
                </Button>
              );
            })}
          </Group>
        </Card>
      )}

      {/* Compliance summary */}
      {complianceFrameworks.length > 0 && (
        <Card withBorder>
          <Text fw={600} mb="xs">Compliance</Text>
          <Group gap="xs">
            {complianceFrameworks.map((fw) => (
              <Badge key={fw} variant="light" color="gray" size="lg">{fw}</Badge>
            ))}
          </Group>
          <Text size="sm" c="dimmed" mt="xs">
            All data in this solution is subject to the above compliance frameworks.
            Operations are audited and retention policies enforced automatically.
          </Text>
        </Card>
      )}
    </Stack>
  );
}

export default ModuleOverview;
